import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Camera } from '../../cameras/api/types';
import { LiveStreamDemo } from './LiveStreamDemo';
import { useShareCodeMutation } from '../api/livestreamingApi';

interface SetupData {
  quality: string;
  duration: number;
  includeAudio: boolean;
  scheduleHours: number[];
  scheduleDays: string[];
}

interface SuccessStepProps {
  selectedCamera: Camera | null;
  setupData: SetupData;
}

export const SuccessStep = ({ selectedCamera, setupData }: SuccessStepProps) => {
  const successSectionRef = useRef<HTMLDivElement>(null);
  const [copyButtonText, setCopyButtonText] = useState('Copy Code');
  const [emailAddress, setEmailAddress] = useState('');
  const [shareButtonText, setShareButtonText] = useState('Share via Email');
  const [isSharing, setIsSharing] = useState(false);
  // New state for quick share modal
  const [showQuickShareModal, setShowQuickShareModal] = useState(false);
  const [quickShareEmail, setQuickShareEmail] = useState('');
  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  
  // Share code mutation
  const [shareCode] = useShareCodeMutation();

  // Ensure we start at the top of the success page with proper timing
  useEffect(() => {
    // Use setTimeout to ensure all content is rendered before scrolling
    const scrollTimer = setTimeout(() => {
      if (successSectionRef.current) {
        // Use scrollIntoView for more reliable positioning
        successSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      } else {
        // Fallback to window.scrollTo
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(scrollTimer);
  }, []);

  const getHTMLCode = (cameraId: string) => {
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0"/>
  <title>Live Camera Stream</title>
  <style>
    .video-wrapper {
      width: 100%;
      max-width: 720px;
      background-color: #000;
      border-radius: 10px;
      overflow: hidden;
      margin: 0 auto;
    }

    video {
      width: 100%;
      height: auto;
      display: block;
    }

    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 2rem;
      text-align: center;
    }

    .spinner {
      border: 4px solid #f3f3f3;
      border-top: 4px solid #0088cc;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      animation: spin 1s linear infinite;
      margin-bottom: 1rem;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  </style>
</head>
<body>
  <div class="video-wrapper">
    <video id="cameraStream" autoplay loop muted playsinline style="display: none;"></video>
    <div id="loadingContainer" class="loading-container">
      <div class="spinner"></div>
      <div>Loading livestream...</div>
    </div>
  </div>

  <script>
    fetch("https://nk6jtwgw3l.execute-api.eu-west-2.amazonaws.com/farmstream/internal/livestream-url?camera_id=${cameraId}")
      .then(res => res.ok ? res.text() : Promise.reject())
      .then(url => {
        document.getElementById("cameraStream").src = url;
        document.getElementById("cameraStream").style.display = "block";
        document.getElementById("loadingContainer").style.display = "none";
      })
      .catch(() => {
        document.getElementById("loadingContainer").innerHTML = "<div>Could not load livestream</div>";
      });
  </script>
</body>
</html>`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyButtonText('Copied!');
      setTimeout(() => {
        setCopyButtonText('Copy Code');
      }, 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
      setCopyButtonText('Copy Failed');
      setTimeout(() => {
        setCopyButtonText('Copy Code');
      }, 2000);
    }
  };

  const handleShareCode = async () => {
    if (!emailAddress) return;

    setIsSharing(true);
    setShareButtonText('Sharing...');

    try {
      // Call the shareCode mutation
      await shareCode({ 
        camera_id: selectedCamera?.SIM_ICCID || '',
        email_address: emailAddress, 
        html_code: getHTMLCode(selectedCamera?.SIM_ICCID || '')
      }).unwrap();

      setShareButtonText('Shared!');
      setEmailAddress(''); // Clear email field after successful sharing

      // Reset button text after 2 seconds
      setTimeout(() => {
        setShareButtonText('Share via Email');
      }, 2000);
    } catch (error) {
      console.error('Failed to share code: ', error);
      setShareButtonText('Share Failed');
      
      // Reset button text after 2 seconds
      setTimeout(() => {
        setShareButtonText('Share via Email');
      }, 2000);
    } finally {
      setIsSharing(false);
    }
  };

  const handleQuickShare = async () => {
    if (!quickShareEmail) return;

    setIsSharing(true);

    try {
      // Call the shareCode mutation
      await shareCode({ 
        camera_id: selectedCamera?.SIM_ICCID || '',
        email_address: quickShareEmail, 
        html_code: getHTMLCode(selectedCamera?.SIM_ICCID || '')
      }).unwrap();

      // Close the modal and reset email
      setShowQuickShareModal(false);
      setQuickShareEmail('');
      
      // Show success notification
      setShowSuccessNotification(true);
      
      // Hide success notification after 3 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 3000);
      
    } catch (error) {
      console.error('Failed to share code: ', error);
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <>
      <SuccessSection ref={successSectionRef}>
        <SuccessTitle>Live Stream Active</SuccessTitle>
        <SuccessText>
          Your camera "{selectedCamera?.nicknameInDatabase}" is now streaming.
        </SuccessText>
        
        <LivestreamContainer>
          <LiveStreamDemo cameraId={selectedCamera?.SIM_ICCID || ''} />
          <LivestreamInfo>
            <LivestreamDetail>Camera ID: {selectedCamera?.SIM_ICCID}</LivestreamDetail>
            <LivestreamDetail>Quality: {setupData.quality}</LivestreamDetail>
          
          </LivestreamInfo>
        </LivestreamContainer>
        
        <SuccessText>
          This is a preview stream and will only last for 5 seconds. 
        </SuccessText>
        
        
        <ImportantNotice>
          <NoticeTitle>⚠️ Important</NoticeTitle>
          <NoticeText>
            <strong>This is a New Feature:</strong> If you cannot see your camera preview above, please contact us at support@farmstream.co.uk for manual configuration assistance.
          </NoticeText>
          <NoticeText>
            <strong>Auto-Disable Policy:</strong> If the livestream code is not added to your website within 10 days, we will automatically turn off the streaming schedule to reduce associated costs.
          </NoticeText>
          <NoticeText>
            <strong>Wrong Time:</strong> If your camera's time is not correct, please update from the camera's app.
          </NoticeText>

        </ImportantNotice>

      </SuccessSection>

      <CodeSection>
        <CodeContainer>
          <CodeTitle>Code for Your Website Manager</CodeTitle>
          <CodeDescription>
            Copy the following code and share it with whoever manages your website to embed your livestream. Please ensure it's added within 10 days to keep your streaming active.
          </CodeDescription>
          <CodeButtonsContainer>
            <CopyButton onClick={() => copyToClipboard(getHTMLCode(selectedCamera?.SIM_ICCID || ''))}>
              {copyButtonText}
            </CopyButton>
            <ShareButton 
              onClick={() => setShowQuickShareModal(true)}
              disabled={isSharing}
            >
              {shareButtonText}
            </ShareButton>
          </CodeButtonsContainer>
          <CodeBox>
            <CodeText>{getHTMLCode(selectedCamera?.SIM_ICCID || '')}</CodeText>
          </CodeBox>
        </CodeContainer>
      </CodeSection>

      <EmailSection>
        <EmailContainer>
          <EmailTitle>Share Livestream Access</EmailTitle>
          <EmailDescription>
            Enter the email address of the person you want to share the livestream access with. They will receive an email with the code and instructions.
          </EmailDescription>
          <EmailInputContainer>
            <EmailInput 
              type="email" 
              placeholder="Enter email address" 
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              disabled={isSharing}
            />
            <ShareButton 
              onClick={handleShareCode}
              disabled={isSharing}
            >
              {shareButtonText}
            </ShareButton>
          </EmailInputContainer>
        </EmailContainer>
      </EmailSection>

        <ButtonsContainer>
          <Button onClick={() => window.location.href = '/dashboard'}>
            Back to Dashboard
          </Button>
        </ButtonsContainer>


      {/* Quick Share Modal */}
      {showQuickShareModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Quick Share Livestream</ModalTitle>
            <ModalDescription>
              Enter the email address for quick sharing of the livestream code.
            </ModalDescription>
            <ModalInputContainer>
              <ModalInput 
                type="email" 
                placeholder="Enter email address" 
                value={quickShareEmail}
                onChange={(e) => setQuickShareEmail(e.target.value)}
                disabled={isSharing}
              />
              <ModalButtonContainer>
                <ModalButton 
                  onClick={handleQuickShare}
                  disabled={isSharing}
                >
                  {isSharing ? 'Sharing...' : 'Share'}
                </ModalButton>
                <ModalButton 
                  onClick={() => setShowQuickShareModal(false)}
                  variant="secondary"
                >
                  Cancel
                </ModalButton>
              </ModalButtonContainer>
            </ModalInputContainer>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Success Notification */}
      {showSuccessNotification && (
        <SuccessNotification>
          <SuccessIcon>✓</SuccessIcon>
          <SuccessMessage>Livestream code shared successfully!</SuccessMessage>
        </SuccessNotification>
      )}
    </>
  );
};

// Styled Components
const SuccessSection = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.green900}10, ${({ theme }) => theme.colors.green900}20);
  border: 1px solid ${({ theme }) => theme.colors.green900}30;
  border-radius: 1rem;
  scroll-margin-top: 2rem; /* Ensures proper spacing when scrolling to this element */
  position: relative; /* Establishes positioning context */
`;

const SuccessTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.green900};
  margin-bottom: 1rem;
`;

const SuccessText = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;
  line-height: 1.6;
`;

const LivestreamContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}30;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin: 2rem 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const LivestreamTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;
  text-align: center;
`;

const LivestreamInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background: ${({ theme }) => theme.colors.gray}10;
  border-radius: 0.5rem;
`;

const LivestreamDetail = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.gray900};
  
  strong {
    color: ${({ theme }) => theme.colors.black};
    font-weight: 600;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  margin-top: 1.5rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const CodeSection = styled.div`
  margin-top: 2rem;
`;

const CodeContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: left;
`;

const CodeTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 0.5rem;
  text-align: center;
`;

const CodeDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const CodeButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const CodeBox = styled.div`
  position: relative;
  background: ${({ theme }) => theme.colors.black};
  border-radius: 0.5rem;
  padding: 1.5rem;
  border: 1px solid ${({ theme }) => theme.colors.gray500}30;
  overflow: hidden;
`;

const CodeText = styled.pre`
  color: ${({ theme }) => theme.colors.white};
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

const CopyButton = styled.button<{ children: string }>`
  background: ${({ theme, children }) => 
    children === 'Copied!' ? theme.colors.green900 : 
    children === 'Copy Failed' ? '#dc3545' : 
    theme.colors.green900};
  color: white;
  border: none;
  padding: 0.75rem 1.25rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 100px; /* Prevent button size changes */

  &:hover {
    background: ${({ theme, children }) => 
      children === 'Copied!' ? theme.colors.green900 : 
      children === 'Copy Failed' ? '#c82333' : 
      theme.colors.green900 + 'dd'};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ShareButton = styled(Button)`
  flex-shrink: 0;
  min-width: 120px;
`;

const ImportantNotice = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.orange}10, ${({ theme }) => theme.colors.orange}20);
  border: 1px solid ${({ theme }) => theme.colors.orange}40;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin: 1.5rem 0;
  text-align: left;
`;

const NoticeTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.orange};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NoticeText = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.black};
  margin: 0 0 0.75rem 0;
  line-height: 1.5;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  strong {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.orange};
  }
`;

const TimeNotice = styled.div`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}08, ${({ theme }) => theme.colors.primary}15);
  border: 1px solid ${({ theme }) => theme.colors.primary}25;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
  font-weight: 500;
`;

const EmailSection = styled.div`
  margin-top: 2rem;
`;

const EmailContainer = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.gray500}20;
  border-radius: 0.75rem;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  text-align: left;
`;

const EmailTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 0.5rem;
  text-align: center;
`;

const EmailDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const EmailInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const EmailInput = styled(Input)`
  flex: 1;
  min-width: 200px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 0.75rem;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;
  text-align: center;
`;

const ModalDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ModalInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ModalInput = styled(Input)`
  width: 100%;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const ModalButton = styled(Button)`
  flex: 1;
`;

const SuccessNotification = styled.div`
  position: fixed;
  top: 2rem;
  right: 2rem;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.green900}f0, ${({ theme }) => theme.colors.green900});
  border: 1px solid ${({ theme }) => theme.colors.green900}30;
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 1001;
  animation: slideIn 0.3s ease-out;
  max-width: 300px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    left: 1rem;
    right: 1rem;
    top: 1rem;
    max-width: none;
  }
`;

const SuccessIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const SuccessMessage = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
  line-height: 1.4;
`;
